import React, { useState, useRef, useEffect } from 'react';
import Style from './table.tailwind.js';

export function ScrollIndicator({ children, className = "" }) {
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);
  const [showRightIndicator, setShowRightIndicator] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);
  const containerRef = useRef(null);

  const checkScrollIndicators = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      
      // Verificar si hay contenido scrolleable
      const hasContent = scrollWidth > clientWidth;
      setHasScrollableContent(hasContent);
      
      // Mostrar indicador izquierdo si no estamos al inicio
      setShowLeftIndicator(scrollLeft > 5);
      
      // Mostrar indicador derecho si hay contenido oculto a la derecha
      setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 5);
      
      // Mostrar hint inicial solo si hay scroll disponible y estamos al inicio
      setShowScrollHint(hasContent && scrollLeft === 0);
    }
  };

  useEffect(() => {
    checkScrollIndicators();
    
    // Re-check cuando se redimensiona la ventana
    const handleResize = () => {
      setTimeout(checkScrollIndicators, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [children]);

  // Efecto adicional para verificar después de que el contenido se renderice completamente
  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollIndicators();
    }, 100); // Pequeño delay para asegurar que el DOM esté completamente renderizado
    
    return () => clearTimeout(timer);
  }, [children]);

  useEffect(() => {
    // Ocultar hint después de 3 segundos
    if (showScrollHint) {
      const timer = setTimeout(() => setShowScrollHint(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showScrollHint]);

  const handleScroll = () => {
    checkScrollIndicators();
    // Ocultar hint al hacer scroll
    setShowScrollHint(false);
  };

  return (
    <div className={`${Style.scrollIndicatorContainer} ${className}`}>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className={Style.scrollContainer}
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          // Opción 3: Efecto de contenido cortado - muestra un poquito de la siguiente columna
          paddingRight: '15px',
          marginRight: '-15px'
        }}
      >
        {children}
      </div>
      
      {/* Indicador gradiente izquierdo */}
      <div className={`${Style.scrollIndicatorLeft} ${showLeftIndicator ? Style.scrollIndicatorVisible : ''}`} 
           style={{ 
             background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0.97) 30%, rgba(255,255,255,0.92) 55%, rgba(255,255,255,0.7) 75%, transparent 100%)',
             width: '48px', // más ancho
           }} />
      
      {/* Indicador gradiente derecho */}
      <div className={`${Style.scrollIndicatorRight} ${showRightIndicator ? Style.scrollIndicatorVisible : ''}`}
           style={{ 
             background: 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0.97) 30%, rgba(255,255,255,0.92) 55%, rgba(255,255,255,0.7) 75%, transparent 100%)',
             width: '48px', // más ancho
           }} />

      {/* Opción 2: Flecha flotante sutil */}
      {(showRightIndicator || (hasScrollableContent && !showLeftIndicator)) && (
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 animate-bounce text-lg opacity-60 pointer-events-none">
          →
        </div>
      )}

      {/* Hint de scroll inicial */}
      {showScrollHint && (
        <div className={Style.scrollHint}>
          ← → Deslizar
        </div>
      )}
    </div>
  );
}
